import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useCart } from "../../utils/cartContext";
import { useAuth } from "../../utils/AuthContext";
import "./CartScreen.css";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { toast } from "react-toastify";
import { url } from "../../Constant/TextConstant";
import { encryptData } from "../../utils/encryption";
import { apiFetch } from "../../utils/apiClient.js";

const CartItem = React.memo(
  ({
    item,
    onUpdateQuantity,
    onRemoveItem,
    updatingItems,
    removingItems,
    isSyncing,
    onClickItem,
    onClickStore,
  }) => {
    const handleProductClick = useCallback(
      (e) => {
        // Only navigate if clicking on the main item area (not buttons or store section)
        if (
          e.target.className.includes("cart-item") ||
          e.target.className.includes("item-details") ||
          e.target.tagName === "H3"
        ) {
          onClickItem(item);
        }
      },
      [onClickItem, item]
    );

    const handleStoreClick = useCallback(
      (e) => {
        e.stopPropagation();
        if (item.StoreMobile) {
          onClickStore(item.StoreMobile, item.StoreName);
        } else {
          toast.error("Store information not available");
        }
      },
      [onClickStore, item.StoreMobile, item.StoreName]
    );

    const itemTotal = useMemo(
      () => item.Price * item.Quantity,
      [item.Price, item.Quantity]
    );
    const hasDiscount = useMemo(
      () => item.DiscountPercentage > 0,
      [item.DiscountPercentage]
    );
    const hasMrp = useMemo(
      () => item.Mrp && item.Mrp > item.Price,
      [item.Mrp, item.Price]
    );

    return (
      <div className="cart-item" onClick={handleProductClick}>
        <div className="item-image">
          <img
            src={
              item.Image1
                ? `https://nearmeind.com/AdminAssets/images/${item.Image1}`
                : "https://via.placeholder.com/150"
            }
            alt={item.ProductName}
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/150";
            }}
            loading="lazy"
          />
        </div>
        <div className="item-details">
          <h3>{item.ProductName}</h3>
          <div className="price-section">
            <span className="item-price">₹{item.Price}</span>
            {hasMrp && <span className="original-price">₹{item.Mrp}</span>}
            {hasDiscount && (
              <span className="discount-badge">
                {item.DiscountPercentage}% OFF
              </span>
            )}
          </div>

          {item.Size && item.Size !== "1piece" && (
            <p className="item-attribute">
              <span>Size:</span> {item.Size}
            </p>
          )}

          {item.ColorName && (
            <p className="item-attribute">
              <span>Color:</span> {item.ColorName}
            </p>
          )}

          <div className="store-section">
            <p className="store-info">
              <span>Sold by:</span> {item.StoreName}
            </p>
            <button
              className="visit-store-btn"
              onClick={handleStoreClick}
              disabled={!item.StoreMobile}
            >
              Visit Store
            </button>
          </div>

          <div className="item-actions">
            <div className="quantity-control">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateQuantity(item.ProductId, item.Quantity - 1);
                }}
                disabled={
                  item.Quantity <= 1 ||
                  updatingItems[item.ProductId] ||
                  isSyncing
                }
                aria-label="Decrease quantity"
              >
                {updatingItems[item.ProductId] ? "..." : "−"}
              </button>
              <span>
                {updatingItems[item.ProductId] ? "..." : item.Quantity}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateQuantity(item.ProductId, item.Quantity + 1);
                }}
                disabled={updatingItems[item.ProductId] || isSyncing}
                aria-label="Increase quantity"
              >
                {updatingItems[item.ProductId] ? "..." : "+"}
              </button>
            </div>

            <button
              className="remove-item"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveItem(item.ProductId);
              }}
              disabled={removingItems[item.ProductId] || isSyncing}
            >
              {removingItems[item.ProductId] ? "Removing..." : "Remove"}
            </button>
          </div>
        </div>
        <div className="item-total">₹{itemTotal}</div>
      </div>
    );
  }
);

const CartScreen = () => {
  const {
    cart: contextCart,
    removeFromCart: contextRemoveFromCart,
    updateQuantity: contextUpdateQuantity,
    isSyncing,
  } = useCart();

  const { isAuthenticated: isLoggedIn, currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localCart, setLocalCart] = useState([]);
  const [updatingItems, setUpdatingItems] = useState({});
  const [removingItems, setRemovingItems] = useState({});
  const baseApiURL = url.baseAPIUrl;

  const createProductSlug = useCallback((productName) => {
    return productName
      .toLowerCase()
      .trim()
      .replace(/%/g, "-percent-")
      .replace(/\+/g, "-plus-")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 60)
      .replace(/-$/, "");
  }, []);

  const handleProductClick = useCallback(
    (product) => {
      const slug = createProductSlug(product.ProductName);
      navigate(`/product/${product.ID}/${slug}`, {
        state: { product },
      });
    },
    [navigate, createProductSlug]
  );

  const handleStoreVisit = useCallback(
    (storeMobile, storeName) => {
      if (!storeMobile) {
        toast.error("Store information not available");
        return;
      }
      navigate(`/store-detail/${encryptData(storeMobile)}`, {
        state: { storeName },
      });
    },
    [navigate]
  );

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);
      
  const fetchCartItems = useCallback(async () => {
    if (!currentUser || !isLoggedIn) {
      if (contextCart && contextCart.length > 0) {
        setLocalCart(contextCart);
      }
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiFetch(
        `${baseApiURL}/api/cartitems/GetCartItems/${currentUser}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch cart items: ${response.status}`);
      }

      const data = await response.json();
      setLocalCart(data);
    } catch (error) {
      setError("Failed to load cart items. Please try again.");

      if (contextCart && contextCart.length > 0) {
        setLocalCart(contextCart);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, isLoggedIn, contextCart, baseApiURL]);

  const optimisticUpdate = useCallback((productId, newQuantity) => {
    setLocalCart((prev) =>
      prev.map((item) =>
        item.ProductId === productId ? { ...item, Quantity: newQuantity } : item
      )
    );
    setUpdatingItems((prev) => ({ ...prev, [productId]: true }));
  }, []);

  const optimisticRemove = useCallback((productId) => {
    setLocalCart((prev) => prev.filter((item) => item.ProductId !== productId));
    setRemovingItems((prev) => ({ ...prev, [productId]: true }));
  }, []);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  const handleQuantityUpdate = useCallback(
    async (productId, newQuantity) => {
      if (newQuantity < 1) return;
      if (updatingItems[productId]) return;

      try {
        optimisticUpdate(productId, newQuantity);

        const itemToUpdate = localCart.find(
          (item) => item.ProductId === productId
        );
        if (!itemToUpdate) return;

        const response = await apiFetch(
          `${baseApiURL}/api/cartitems/UpdateCartItem`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              Id: itemToUpdate.Id,
              Quantity: newQuantity,
              Total: itemToUpdate.Price * newQuantity,
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to update quantity");

        await contextUpdateQuantity(productId, newQuantity);
        await fetchCartItems();
        toast.success("Quantity updated");
      } catch (error) {
        await fetchCartItems();
        toast.error("Failed to update quantity");
      } finally {
        setUpdatingItems((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [
      baseApiURL,
      contextUpdateQuantity,
      fetchCartItems,
      localCart,
      optimisticUpdate,
      updatingItems,
    ]
  );

  const handleRemoveItem = useCallback(
    async (productId) => {
      if (removingItems[productId]) return;

      try {
        optimisticRemove(productId);

        const itemToRemove = localCart.find(
          (item) => item.ProductId === productId
        );
        if (!itemToRemove) return;

        const response = await apiFetch(
          `${baseApiURL}/api/cartitems/DeleteCartItem/${itemToRemove.Id}`,
          {
            method: "POST",
          }
        );

        if (!response.ok) throw new Error("Failed to remove item");

        await contextRemoveFromCart(productId);
        await fetchCartItems();
      } catch (error) {
        await fetchCartItems();
        toast.error("Failed to remove item");
      } finally {
        setRemovingItems((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [
      baseApiURL,
      contextRemoveFromCart,
      fetchCartItems,
      localCart,
      optimisticRemove,
      removingItems,
    ]
  );

  const handleCheckout = useCallback(() => {
    if (!isLoggedIn) {
      const shouldLogin = window.confirm(
        "You need to login first. Go to login page?"
      );
      if (shouldLogin) navigate("/login", { state: { from: "/cart" } });
      return;
    }

    if (localCart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    navigate("/checkout", {
      state: {
        cart: localCart,
        cartTotal: localCart.reduce(
          (sum, item) => sum + item.Price * item.Quantity,
          0
        ),
        cartCount: localCart.reduce((sum, item) => sum + item.Quantity, 0),
      },
    });
  }, [isLoggedIn, localCart, navigate]);

  const [displayCartTotal, displayCartCount] = useMemo(() => {
    const total = localCart.reduce(
      (sum, item) => sum + item.Price * item.Quantity,
      0
    );
    const count = localCart.reduce((sum, item) => sum + item.Quantity, 0);
    return [total, count];
  }, [localCart]);

  const renderCartItems = useCallback(() => {
    return localCart.map((item) => (
      <CartItem
        key={`${item.Id}-${item.ProductId}`}
        item={item}
        onUpdateQuantity={handleQuantityUpdate}
        onRemoveItem={handleRemoveItem}
        updatingItems={updatingItems}
        removingItems={removingItems}
        isSyncing={isSyncing}
        onClickItem={handleProductClick}
        onClickStore={handleStoreVisit}
      />
    ));
  }, [
    localCart,
    handleQuantityUpdate,
    handleRemoveItem,
    updatingItems,
    removingItems,
    isSyncing,
    handleProductClick,
    handleStoreVisit,
  ]);

  if (isLoading || isSyncing) {
    return (
      <>
        <Helmet>
          <title>{`Loading Cart... | NearMe Store`}</title>
        </Helmet>
        <Header />
        <div className="loading-container">
          <div className="loader"></div>
          <p>{isSyncing ? "Syncing your cart..." : "Loading your cart..."}</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{`Error | NearMe Store`}</title>
        </Helmet>
        <Header />
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Cart</h2>
          <p>{error}</p>
          <div className="action-buttons">
            <button
              onClick={() => window.location.reload()}
              className="retry-button"
            >
              Try Again
            </button>
            <Link to="/" className="continue-shopping">
              Continue Shopping
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (localCart.length === 0) {
    return (
      <>
        <Helmet>
          <title>{`Empty Cart | NearMe Store`}</title>
          <meta
            name="description"
            content="Your shopping cart is currently empty. Continue shopping to add items."
          />
        </Helmet>
        <Header />
        <div className="empty-cart">
          <div className="empty-cart-icon">🛒</div>
          <h2>Your Cart is Empty</h2>
          <p>Looks like you haven't added any items yet</p>
          <Link to="/" className="continue-shopping">
            Continue Shopping
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Your Cart (${displayCartCount} items) | NearMe Store`}</title>
        <meta
          name="description"
          content={`Review your ${displayCartCount} items in cart. Total: ₹${displayCartTotal}`}
        />
      </Helmet>

      <Header />

      <div className="cart-screen">
        <div className="cart-header">
          <h1>Your Shopping Cart</h1>
          <span className="item-count">{displayCartCount} items</span>
        </div>

        <div className="cart-container">
          <div className="cart-items">{renderCartItems()}</div>

          <div className="cart-summary">
            <div className="summary-card">
              <h2>Order Summary</h2>

              <div className="summary-row">
                <span>Subtotal ({displayCartCount} items)</span>
                <span>₹{displayCartTotal}</span>
              </div>

              <div className="summary-row">
                <span>Delivery Charges</span>
                <span>FREE</span>
              </div>

              <div className="summary-row total">
                <span>Total Amount</span>
                <span>₹{displayCartTotal}</span>
              </div>

              <button
                className={`checkout-btn ${
                  !isLoggedIn ? "login-required" : ""
                }`}
                onClick={handleCheckout}
                disabled={isLoading || isSyncing || localCart.length === 0}
              >
                {isLoading || isSyncing
                  ? "Processing..."
                  : isLoggedIn
                  ? "Proceed to Checkout"
                  : "Login to Checkout"}
              </button>

              <Link to="/" className="continue-shopping">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CartScreen;
